import { gql, useLazyQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import * as React from 'react';

import { logWarning } from '@/imports/logging/ClientLogger';

import {
  useDownloadOrganizationCsvErrors_getErrorsForOrganizationCsvUpload,
  useDownloadOrganizationCsvErrors_getErrorsForOrganizationCsvUploadVariables,
} from './graphql-types/useDownloadOrganizationCsvErrors_getErrorsForOrganizationCsvUpload';

import { useDownloadErrorFile } from './useDownloadErrorFile';

const USER_CSV_UPLOAD_ERROR = gql`
  query useDownloadOrganizationCsvErrors_getErrorsForOrganizationCsvUpload(
    $input: GetErrorsForOrganizationCsvUploadInput!
  ) {
    getErrorsForOrganizationCsvUpload(input: $input) {
      errorFileUrl
    }
  }
`;

export const useDownloadCompaniesCsvErrors = (options: {
  validRoutes: string[];
}): void => {
  const router = useRouter();
  const organizationCsvUploadId = router.query.organizationCsvUploadId;

  const isValidRoute = options.validRoutes.reduce(
    (prev, route) => prev || router.pathname.endsWith(route),
    false
  );

  const [getErrorsForOrganizationCsvUpload, { data, called }] = useLazyQuery<
    useDownloadOrganizationCsvErrors_getErrorsForOrganizationCsvUpload,
    useDownloadOrganizationCsvErrors_getErrorsForOrganizationCsvUploadVariables
  >(USER_CSV_UPLOAD_ERROR);

  React.useEffect(() => {
    if (
      !router.isReady ||
      !organizationCsvUploadId ||
      called ||
      !isValidRoute
    ) {
      return;
    }

    getErrorsForOrganizationCsvUpload({
      variables: {
        input: {
          data: { organizationCsvUploadId: organizationCsvUploadId as string },
        },
      },
    });
  }, [
    router,
    called,
    organizationCsvUploadId,
    getErrorsForOrganizationCsvUpload,
    isValidRoute,
  ]);

  React.useEffect(() => {
    if (data && !data.getErrorsForOrganizationCsvUpload.errorFileUrl) {
      logWarning(
        `Could not find error file for organizationCsvUpload: ${organizationCsvUploadId}`
      );
    }
  }, [data, organizationCsvUploadId]);

  useDownloadErrorFile({
    fileUrl: data?.getErrorsForOrganizationCsvUpload.errorFileUrl ?? undefined,
    shouldDownload: !!organizationCsvUploadId,
  });
};
